import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-09-10 17:22:11 +0200 (Thu, 10 Sep 2026) $
//  Author    : Dirk Rothe, Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 165450 $
//  Purpose   : wiki renderer
// =============================================================================
// ---Third-party imports
import React from 'react';
import { isElement } from 'react-is';
// ---Framework imports
import log from 'uix/logging';
import { sorted, isArray, forOwn, has, merge, each, includes, entries, lstrip, keys, isEqual, dict, mapItems, isNil, isString } from 'ldash';
import { cssToReactStyle } from 'uix/utils';
import connect from 'uix/connect';
import { translate } from 'uix/i18n';
import { selectDomainNamedParts, Select } from 'uix/selectors';
import memoizeOne from 'memoize-one';
import { ErrorBoundary } from 'uix/components/error';
import { useMemo } from 'uix/hooks';
import { PortalTarget } from 'uix/components/portals';
// ---Local imports
import { WikiContext, decodeAttribH5, schemaifyAttributes } from './utils';
import { useWidgetResolving, useWikiTemplates } from './hooks';
// ---Globals
const reactifyAttributes = (attrs)=>{
    const obj = {};
    const skipAttributes = [
        'onclick'
    ];
    const mergeAttributes = {
        class: 'className',
        cellpadding: 'cellPadding',
        cellspacing: 'cellSpacing',
        colspan: 'colSpan',
        'col-span': 'colSpan',
        rowspan: 'rowSpan',
        'row-span': 'rowSpan',
        formstyle: 'formStyle'
    };
    forOwn(attrs, (value, k)=>{
        // key starts with capital letter => do lower case to avoid warnings in the console
        let key = k.match(new RegExp(/^[A-Z]/)) !== null ? k.toLowerCase() : k;
        if (key == 'default') {
            value = value.toLowerCase() == 'true';
        }
        if (skipAttributes.includes(key)) {
            return;
        }
        if (key == '{http://www.w3.org/1999/xlink}href') {
            // will be understood by react
            key = 'xlinkHref';
        } else if (key.indexOf(':') !== -1) {
            // unhandled namespace
            return;
        }
        let attrs = value;
        if (isArray(value)) {
            attrs = join(value, ' ');
        }
        if (key === 'style') {
            attrs = cssToReactStyle(attrs);
        }
        if (has(mergeAttributes, key)) {
            key = mergeAttributes[key];
        }
        merge(obj, {
            [key]: attrs
        });
    });
    return obj;
};
const reactifyWhitespace = (tag, parent, text, tail)=>{
    /* react has rules for textnodes
  - Whitespace text nodes cannot appear as a child of <tr>
  - Whitespace text nodes cannot appear as a child of <tbody>
  */ if (tag == 'tr' || tag == 'table' || tag == 'td') {
        if (tag !== 'td') {
            text = null;
        }
        if (tag == 'tr' || tag == 'td') {
            tail = null;
        }
    }
    return {
        text,
        tail
    };
};
let partNameVariants = memoizeOne((namedParts)=>{
    log.debug('rebuild templates II');
    return new Set([
        ...keys(namedParts),
        ...keys(namedParts).map((n)=>n.toLowerCase())
    ]);
}, isEqual);
const storeMap = (root, props)=>({
        apiurl: Select.apiurl(root),
        dname: root.meta.request.dname,
        iconfig: Select.iconfig(root),
        dconfig: Select.dconfig(root),
        ddata: root.config.domains.data[root.meta.request.dname],
        language: root.meta.language,
        i18n: Select.i18n(root),
        user: Select.user(root),
        namedParts: selectDomainNamedParts(root)
    });
const WikiPaneContent = (props)=>{
    let { rendered, elements, dispatch, namedParts, dconfig, iconfig, ddata, language, wikipaneId = null, wikiTemplates, sortedTemplates, ...otherProps } = props;
    const translateText = (identifier)=>translate(props.i18n, lstrip(identifier, '_'));
    const hasGroup = (group)=>{
        let { user, dname } = props;
        let { localgroups, groups } = user;
        return Boolean(includes(groups, group) || localgroups && includes(localgroups[dname], group));
    };
    let rootTextNode = null;
    if (rendered) {
        rootTextNode = rendered.$text;
        elements = rendered.$children;
        if (!wikipaneId) {
            wikipaneId = rendered.otid || rendered.identifier;
        }
    }
    let root = {
        tag: '',
        children: [],
        parent: null,
        i: 0
    };
    let keys = {
        byTag: {
            '': root
        },
        byClassName: {}
    };
    otherProps = {
        ...otherProps,
        // including lowercase variants here is disputable
        partNames: partNameVariants(namedParts)
    };
    // if a wiki element fetches raw wiki-json (in $tag, $children form) - we need to transform it into a normalized form
    let buildNodeSet = (rawNodes)=>sanitize(isArray(rawNodes) ? rawNodes : [
            rawNodes
        ], root, {}, {});
    // utility functions
    let util = {
        translate: translateText,
        hasGroup,
        dispatch,
        buildNodeSet
    };
    const applyTemplates = (elements)=>{
        let transformed = [];
        for (let element of elements){
            if (!element) continue; // ignore null/undefineds if manually calling applyTemplates
            let { children, parent, tail } = element;
            let _apply = (children)=>children ? applyTemplates(isArray(children) ? children : [
                    children
                ]) : [
                    element.text,
                    applyTemplates(element.children)
                ];
            let args = {
                ...element,
                node: element,
                parent: parent.deref(),
                // non xml tree
                keys,
                util,
                ddata,
                dconfig,
                iconfig,
                language,
                wikipaneId,
                data: otherProps,
                applyTemplates: _apply,
                templates: wikiTemplates
            };
            let result = null;
            let template = null;
            if (activeTagMatchers[element.tag]) {
                // simple tagMatchers are optimized here (no list scan)
                template = activeTagMatchers[element.tag];
            } else {
                for (let [name, templ] of activeTemplates){
                    let matched = templ.match(args);
                    if (matched) {
                        template = templ;
                        break;
                    }
                }
            }
            if (template) {
                if (template.schema) {
                    args = {
                        ...args,
                        attrib: schemaifyAttributes(args.attrib, template.schema)
                    };
                }
                result = template.build(args);
            }
            if (result) {
                if (isArray(result)) {
                    // the last list item could be a new tail
                    transformed.push(...result);
                } else {
                    transformed.push(result);
                    // if we return a scalar, tail is auto pushed
                    if (tail) {
                        transformed.push(tail);
                    }
                }
            }
        }
        // auto-add key to react elements of transformed
        transformed = transformed.map((e, i)=>{
            if (isElement(e)) {
                return /*#__PURE__*/ React.cloneElement(e, {
                    key: i
                });
            }
            return e;
        });
        return transformed;
    };
    let sanitized = sanitize(elements || [], root, keys.byTag, keys.byClassName);
    root.children.push(...sanitized);
    let initParams = {
        root,
        keys,
        ddata,
        dconfig,
        language,
        data: otherProps,
        applyTemplates
    };
    let activeTagMatchers = {};
    // fill activeTagMatchers as side effect for optimizing applyTemplates
    let activeTemplates = sortedTemplates.filter((t)=>{
        let is_active = !t[1].enabled || t[1].enabled(initParams);
        if (is_active && t[1].tagMatcher) {
            activeTagMatchers[t[1].tagMatcher] = t[1];
        }
        return is_active;
    });
    // initializers are toplevel mounted react comps (for each matching template rule)
    let initializers = activeTemplates.filter((t)=>t[1].init).map(([n, t])=>[
            n,
            t.init(initParams)
        ]).filter((e)=>e[1]).map(([n, initRes])=>/*#__PURE__*/ _jsx(React.Fragment, {
            children: initRes
        }, n));
    let result = applyTemplates(sanitized);
    if (!otherProps.className) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                rootTextNode,
                result,
                initializers
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: otherProps.className,
        children: [
            rootTextNode,
            result,
            initializers
        ]
    });
};
const sanitize = (children, parent, byTag, byClassName)=>/* rename the $-elements to non-$-form
  - filter xml comments ($tag == '')
  - push other attributes into attrib and reactify them
  - add access to parent element (circularities?)
  - ensure children has list form
  - index by tagname
  */ children.filter((e)=>e.$tag !== '').map((e, i)=>{
        let { $tag: tag, $children: children, $text: text, $tail: tail, ...attrib } = e;
        if (tag == '') {
            // comment node
            log.warn('invalid element: %o %o', attrib, text);
            tag = 'span';
        }
        let res_ws = reactifyWhitespace(tag, parent, text, tail);
        text = res_ws.text;
        tail = res_ws.tail;
        let res = {
            tag,
            text,
            tail,
            parent: new WeakRef(parent),
            attrib: reactifyAttributes(attrib),
            i
        };
        // register tag names during parsing
        let tags = byTag[tag];
        if (!tags) {
            tags = [];
            byTag[tag] = tags;
        }
        tags.push(res);
        // register class names during parsing
        if (res.attrib.className) {
            res.attrib.className.split().forEach((cn)=>{
                let cns = byClassName[cn];
                if (!cns) {
                    cns = [];
                    byClassName[cn] = cns;
                }
                cns.push(res);
            });
        }
        res.children = children ? sanitize(children, res, byTag, byClassName) : [];
        return res;
    });
const ConnectedWikiPane = connect('ConnectedWikipane', {
    storeMap,
    dispatchActions: true
}, WikiPaneContent);
export const WikiPane = ({ otid, mobile, identifier, assets, rendered, ...props })=>{
    let wikiTemplates = useWikiTemplates();
    let sortedTemplates = useMemo(()=>sorted(entries({
            ...wikiTemplates
        }), (e)=>-e[1].priority || 0), [
        wikiTemplates
    ]);
    let ctxdata = useMemo(()=>({
            otid,
            mobile,
            identifier,
            assetsById: dict((assets || []).map((e)=>[
                    e.otid,
                    e
                ]))
        }), [
        assets,
        otid,
        identifier,
        mobile
    ]);
    return /*#__PURE__*/ _jsx(ErrorBoundary, {
        relatedErrorId: otid,
        children: /*#__PURE__*/ _jsx(WikiContext.Provider, {
            value: ctxdata,
            children: /*#__PURE__*/ _jsx(ConnectedWikiPane, {
                otid: otid,
                identifier: identifier,
                rendered: rendered,
                wikiTemplates: wikiTemplates,
                sortedTemplates: sortedTemplates,
                ...props
            })
        })
    });
};
